import React from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { FlaskConicalIcon } from 'lucide-react'

import { ROUTES } from 'routes'
import { useCanAccess } from 'hooks'
import { Header } from 'components'
import { Tabs } from '@repo/ui'

import { LABS_HEADER_GAP, LABS_HEADER_H_VAR, labsStickySx, usePublishedHeight } from './constants'
import { BrandLab } from './sections/BrandLab'
import { ComponentsLab } from './sections/ComponentsLab'
import { EmailStructureLab } from './sections/EmailStructureLab'
import { NotificationsLab } from './sections/NotificationsLab'
import { LoadersLab } from './sections/LoadersLab'
import { SectionStyleLab } from './sections/SectionStyleLab'
import { NavChromeLab } from './sections/NavChromeLab'
import { SettingsNavLab } from './sections/SettingsNavLab'
import { ScanSimulatorLab } from './sections/ScanSimulatorLab'

/** Centered "coming soon" panel for sections whose infra hasn't landed yet. */
const ComingSoon = ({ label, description }: { label: string; description: string }) => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 6,
        minHeight: 320,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.paper',
      }}
    >
      <Stack spacing={2} alignItems="center" textAlign="center" maxWidth={420}>
        <FlaskConicalIcon size={32} strokeWidth={1.25} color={theme.palette.text.secondary} />
        <Typography variant="h6">{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Stack>
    </Box>
  )
}

interface LabsSection {
  key: string
  label: string
  render: () => React.ReactNode
}

/**
 * Registry of Labs sections. Adding a section here is the only edit needed to
 * extend Labs (feature flags, seed data, webhook tester…) — the sub-nav and
 * the active panel render off this array.
 */
// Integration votes graduated to /internal/integrations (VERA-405).
// Signups graduated to /internal/signups (VRG-401) — it was an operational QUEUE living in
// the prototype hub, and it now has its own sidebar row in the accounts group. Note the
// knock-on: `LABS_SECTIONS[0]` is the fallback for an absent/unknown `:section`, so bare
// `/internal/labs` now lands on Notifications rather than Signups. That is correct — the
// default should be a real Labs section — but it IS a behaviour change for anyone with the
// old URL bookmarked, and `/internal/labs/signups` now falls back to Notifications rather
// than 404ing.
//
// VRG-512, 2026-08-30: renamed from "Emails" — the section now covers SMS too (every
// template's SMS equivalent, where one exists), so "Emails" undersold what's here. The key
// changed from 'emails' to 'notifications' along with it; `/internal/labs/emails` bookmarks
// now fall through to the same `LABS_SECTIONS[0]` default rather than 404ing, same as any
// other unknown section — consistent with how the Signups rename above was handled.
const LABS_SECTIONS: LabsSection[] = [
  {
    key: 'notifications',
    label: 'Notifications',
    render: () => <NotificationsLab />,
  },
  {
    key: 'components',
    label: 'Components',
    render: () => <ComponentsLab />,
  },
  {
    // VRG-560: how a Vera email is BUILT — the `v-*` dark-mode class contract, the
    // structural rules, and the traps. Sits beside Notifications deliberately: that
    // section shows WHAT each template says, this one shows how any of them is
    // constructed. Its class table is parsed out of a live render rather than typed,
    // so it cannot drift from `base-email.tsx` the way the scratchpad specimen it
    // replaces immediately did.
    key: 'email-structure',
    label: 'Email structure',
    render: () => <EmailStructureLab />,
  },
  {
    // VRG-162: replacement for the removed production scan-bypass buttons —
    // exercise the scan-gated transfer steps (sender confirm / recipient
    // accept) against a real certificate id, non-production only.
    key: 'scan-simulator',
    label: 'Scan simulator',
    render: () => <ScanSimulatorLab />,
  },
  {
    // Candidate page loaders. Prototypes for a decision, not shipped components —
    // the winner gets extracted into @repo/ui once and this section retired.
    key: 'loaders',
    label: 'Loaders',
    render: () => <LoadersLab />,
  },
  {
    // VERA-458: brand-asset gallery — the app icon plus the variants that were
    // considered and rejected, so the decision outlives the session that made it.
    key: 'brand',
    label: 'Brand',
    render: () => <BrandLab />,
  },
  {
    // A design DECISION surface, not a component gallery: four candidate treatments for
    // settings sections, on identical content, so the choice can be looked at.
    key: 'section-style',
    label: 'Section style',
    render: () => <SectionStyleLab />,
  },
  {
    // Also a decision surface: the settings nav's Personal/Company split as tabs (today)
    // against sections, side by side, with the heights measured — the fit is the argument.
    key: 'settings-nav',
    label: 'Settings nav',
    render: () => <SettingsNavLab />,
  },
  {
    // Hover/selected treatment + popover surface, with the contrast maths printed beside each
    // option. Decides what the sectioned nav's selected row should look like.
    key: 'nav-chrome',
    label: 'Nav chrome',
    render: () => <NavChromeLab />,
  },
]

/**
 * /internal/labs — admin-only internal tooling hub.
 *
 * Page title + a horizontal section tab row, then the active section's panel at the
 * page's FULL width.
 *
 * ## Why the section nav is a tab row and not a left column (VRG-560)
 *
 * It was a `md={2}` `SideNav` column mirroring the Settings page. That is the right
 * construction for Settings, whose sections are the page's only navigation. It is the
 * wrong one here, because a Labs section can need columns of its own: Notifications
 * renders a template index, a 600px email preview and a props rail side by side, and the
 * section nav column plus its 24px gutter was taking ~224px off the top of that budget
 * before the section got to lay anything out. Measured at 1280x800 with the sidebar
 * collapsed, the section had 935px to fit 1120px of content in.
 *
 * A tab row spends ~48px of VERTICAL space to give ~224px of HORIZONTAL back, and
 * horizontal is the binding constraint on every multi-column section. Eight short labels
 * fit one row comfortably.
 *
 * The rejected alternative was merging the template index INTO the section nav as a nested
 * list. It fails on both fit and meaning: 47 templates under ~14 category headings is ~61
 * rows against 8, so either the section rows scroll out of reach or the column carries two
 * competing scroll regions — and a section nav is NAVIGATION while a template index is
 * CONTENT SELECTION WITHIN a section. Collapsing the two levels into one list says they are
 * the same kind of thing, and they are not.
 *
 * Shared `@repo/ui` `Tabs`, controlled — the same component the sections themselves use for
 * their own tab rows, so the two rows cannot drift apart (§28/§47).
 */
export const LabsPage = () => {
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const navigate = useNavigate()
  const { section } = useParams<{ section?: string }>()

  /**
   * The header block's measured height, published to the sections so their own sticky
   * sub-headers pin exactly below it.
   *
   * Measured rather than computed — see `LABS_HEADER_H_VAR` for the 29px clipping bug a
   * hardcoded 64 caused, and for why replacing it with a hardcoded 93 would only have
   * restarted the same clock. This surface is its own best argument: adding the tab row
   * below moved this number by ~48px, and nothing here had to be re-derived.
   *
   * Published as a CSS custom property on the CONTENT WRAPPER rather than through React
   * context. The wrapper, not this block, because the property has to reach the sections
   * through the cascade and this block is their sibling, not their ancestor. The rest of
   * the reasoning — why a context default of `0` was the wrong failure mode, and why a
   * variable cannot lose its identity across a Fast Refresh the way a context object can —
   * is on `LABS_HEADER_H_VAR`.
   */
  const headerH = usePublishedHeight(LABS_HEADER_H_VAR)

  // The active section is driven by the URL (`/internal/labs/:section`) so it's
  // deep-linkable + survives refresh. Unknown/absent → default to the first.
  const activeKey = LABS_SECTIONS.some(s => s.key === section) ? (section as string) : LABS_SECTIONS[0].key

  // Labs is platform_admin-only. InternalRoute also admits platform_manager,
  // so guard here too (the sidebar entry is already admin-gated).
  if (!isAdmin) {
    return <Navigate to={ROUTES.INTERNAL} replace />
  }

  const active = LABS_SECTIONS.find(s => s.key === activeKey) ?? LABS_SECTIONS[0]

  return (
    <>
      {/* ── Sticky header block: page title + section tabs ──────────────────────
          ONE sticky element containing both, rather than two pinned in sequence.
          That is what makes the offset measurable in a single number, and it means the
          tab row is still on screen at the bottom of a long section.

          The sticky lives on this wrapper and NOT on `Header`, so the wrapper can be
          measured. A `position: sticky` element cannot be wrapped for measurement — the
          wrapper would become its containing block and it would unstick the moment the
          wrapper scrolled away — so the sticky has to move OUT to the element being
          measured, not the other way round.

          `Header`'s own sx neutralises the -4.5px lift + matching negative margin it
          applies to a title-only page (aligning the title to the sidebar's brand mark,
          §29). Here the header is not the last word in the block — the tab row is — so
          the lift would pull the title off the wrapper's box and make the measured height
          disagree with what is painted. `pb: 3` (12px) rather than the default 24 puts the
          tabs a header-unit's distance below the title instead of a section's.

          `pb: LABS_HEADER_GAP` is the gap down to the section's content, and it lives HERE
          rather than as `pt` on the wrapper below — that is what stops a section's own
          sticky band sliding on scroll. See `LABS_HEADER_GAP`'s docblock for the
          measurement. Because it is inside the MEASURED block, a section pinning at
          `top: var(--labs-header-h)` pins exactly at its own natural position. */}
      <Box ref={headerH.source} sx={{ ...labsStickySx(0, 4), pb: LABS_HEADER_GAP }}>
        <Header title="Labs" sx={{ pb: 3, position: 'static', top: 'auto', marginBottom: 0 }} />
        <Tabs
          tabs={LABS_SECTIONS.map(s => ({ label: s.label, value: s.key }))}
          value={activeKey}
          onChange={key => navigate(`${ROUTES.INTERNAL_LABS}/${key}`)}
          redirect={false}
        />
      </Box>

      {/* No `pt` here — the gap is the header block's `pb` above (LABS_HEADER_GAP). Adding
          one back reintroduces the slide it was moved to fix.

          This wrapper carries `--labs-header-h`, so every section under it inherits the
          page header's live height and can pin its own band at exactly that offset. */}
      <Box ref={headerH.target} sx={{ position: 'relative', zIndex: 1 }}>
        {active.render()}
      </Box>
    </>
  )
}
